import React from 'react'
import { Box, Paper, Typography, makeStyles } from '@material-ui/core'
import useTranslate from '../../../../../i18n/useTranslate'

export type StackedListItem = {
    key: string | number
    label: string
    value: number
    overdue?: number
    labelBg?: string
}

type Props = {
    title: string
    items: StackedListItem[]
    onClickRow?: (item: StackedListItem) => void
    onClickOverdue?: (item: StackedListItem) => void
    labelBg?: string
}

const DEFAULT_LABEL_PALETTE = ['#ffea04', '#379956', '#bababa', '#ffac04', '#ebacc5', '#bfdbfe', '#D9B8FF',  '#b8ffd0']

const StackedListCard: React.FC<Props> = ({ title, items, onClickRow, onClickOverdue, labelBg }) => {
    const classes = useStyles()
    const t = useTranslate()

    return (
        <Paper elevation={0} className={classes.card}>
            <Box className={classes.header}>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Box className={classes.headerRight}>{t.get('UM.PROJECT_DASHBOARD.OVERDUE')}</Box>
            </Box>

            {items.length === 0 ? (
                <Typography variant="body2" color="textSecondary" className={classes.empty}>
                    {t.get('LABEL.NO_DATA_FOUND')}
                </Typography>
            ) : (
                <Box className={classes.body}>
                    {items.map((it, idx) => {
                        const bandBg =
                            it.labelBg ?? labelBg ?? DEFAULT_LABEL_PALETTE[idx % DEFAULT_LABEL_PALETTE.length]
                        const explicit = Boolean(it.labelBg || labelBg)
                        const valueBg = explicit ? '#FFFFFF' : bandBg
                        const valueFg = '#393E46'

                        return (
                            <Box key={String(it.key)} className={classes.row}>
                                <Box className={classes.leftBanded} onClick={() => onClickRow?.(it)}>
                                    <div className={classes.band} style={{ background: bandBg }}>
                                        {it.label}
                                    </div>
                                    <div
                                        className={classes.valueBanded}
                                        style={{ background: valueBg, color: valueFg }}
                                    >
                                        {Number(it.value ?? 0).toLocaleString()}
                                    </div>
                                </Box>

                                <Box className={classes.right} onClick={() => onClickOverdue?.(it)}>
                                    <div className={classes.rightValue}>{Number(it.overdue ?? 0).toLocaleString()}</div>
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            )}
        </Paper>
    )
}

const useStyles = makeStyles((theme) => ({
    card: {
        borderRadius: 12,
        border: '1px solid #E5E7EB',
        background: '#fff',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr 100px',
        alignItems: 'center',
        fontWeight: 700,
        marginBottom: 16,
        gap: 12,
        [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr 88px' },
    },
    headerRight: { textAlign: 'center', color: '#D62E4B', fontSize: 12, fontWeight: 700 },
    body: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 12,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr 100px',
        gap: 12,
        alignItems: 'stretch',
        [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr 88px' },
    },
    leftBanded: {
        borderRadius: 10,
        border: '1px solid #E5E7EB',
        background: '#fff',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform .1s ease, box-shadow .15s ease',
        '&:hover': { boxShadow: '0 1px 6px rgba(0,0,0,.06)' },
    },
    band: {
        padding: theme.spacing(0.5, 2),
        textAlign: 'center',
        fontWeight: 800,
        color: '#000',
    },
    valueBanded: {
        padding: theme.spacing(1, 2),
        textAlign: 'center',
        fontSize: 22,
        fontWeight: 800,
        lineHeight: 1.1,
    },
    right: {
        borderRadius: 10,
        border: '1px solid #E5E7EB',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'box-shadow .15s ease',
        '&:hover': { boxShadow: '0 1px 6px rgba(0,0,0,.06)' },
    },
    rightValue: { fontSize: 18, fontWeight: 800 },
    empty: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: theme.spacing(2),
        borderRadius: 10,
        background: '#F9FAFB',
        border: '1px dashed #E5E7EB',
    },
}))

export default StackedListCard
